package com.arena_web.ui.sections.profile.mobile

import androidx.compose.runtime.*
import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.core.viewmodel.UserProfileViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.di.LocalAppContainer
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.components.widgets.LoadingSpinner
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.px

// =============================================================================
// Mobile "another user's profile" bottom sheet — Figma 8636:332042.
//
// On desktop, visiting another user opens the dedicated /profile/{id} screen.
// On mobile, the same profile slides up as a bottom sheet over the current
// screen. Both surfaces render the SAME read-only mobile profile content
// (`ProfileContentMobile` with `isOwnProfile = false`); the dialog / follow /
// chat wiring lives once in `UserProfileMobile`.
// =============================================================================

/** Global open-state for the mobile profile sheet. A singleton (rather than a
 *  CompositionLocal) so any click handler anywhere can open it without a
 *  provider in scope — the single host in `AppEntry` observes this state. */
object UserProfileSheetController {
    var openUserId by mutableStateOf<String?>(null)
        private set

    fun open(userId: String) {
        openUserId = userId
    }

    fun close() {
        openUserId = null
    }
}

/** Open another user's profile: a bottom sheet on phones, the full
 *  `/profile/{id}` screen on larger viewports. Use this everywhere instead of
 *  navigating to the profile route directly. */
fun openUserProfile(ctx: PageContext, userId: String) {
    if (isMobileViewport()) {
        UserProfileSheetController.open(userId)
    } else {
        ctx.router.navigateTo(Routes.userProfile(userId))
    }
}

/** Mirror of `isMobile()` for non-composable call sites (matches the 600-px
 *  breakpoint used by `ui/utils/Responsive.kt`). */
private fun isMobileViewport(): Boolean = try {
    window.innerWidth < 600
} catch (_: Throwable) {
    false
}

/** Mounted once in `AppEntry`; renders the profile sheet whenever the
 *  controller holds a user id and the viewport is phone-sized. */
@Composable
fun UserProfileSheetHost() {
    val userId = UserProfileSheetController.openUserId ?: return
    // A late resize past the breakpoint (e.g. rotate to a wide tablet) — drop
    // the sheet; the user can re-open via the desktop route.
    if (!isMobileViewport()) {
        UserProfileSheetController.close()
        return
    }

    // The host sits outside PageLayout (which is what normally provides the
    // sport palette), so supply it here from the selected sport.
    val sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all"
    CompositionLocalProvider(LocalSportColors provides SportColors.forSport(sportName)) {
        BottomSheet(
            onDismiss = { UserProfileSheetController.close() },
            // Profiles are content-heavy and always scroll — open at full height.
            fullHeight = true,
        ) {
            UserProfileMobile(
                userId = userId,
                onDismiss = { UserProfileSheetController.close() },
            )
        }
    }
}

/** Read-only mobile profile for [userId], plus the follow / unfollow / chat and
 *  followers/following-list wiring. Shared by [UserProfileSheetHost] (inside a
 *  bottom sheet, [onDismiss] non-null) and the `/profile/{id}` page's mobile
 *  branch ([onDismiss] null → full-page). */
@Composable
fun UserProfileMobile(
    userId: String,
    onDismiss: (() -> Unit)? = null,
    externalViewModel: UserProfileViewModel? = null,
) {
    val t = LocalAppStrings.current
    val container = LocalAppContainer.current
    // The sheet host builds its own VM; the /profile/{id} page hands in the one
    // it already owns (so sport-switch refreshes the same instance).
    val viewModel = externalViewModel ?: rememberViewModel(key = userId) {
        UserProfileViewModel(
            userId = userId,
            userRepository = container.publicUserRepository,
            teamRepository = container.publicTeamRepository,
            followRepository = container.followRepository,
            eventRepository = container.publicEventRepository,
            cardRepository = container.cardRepository,
        )
    }
    val state by viewModel.uiState.collectAsState()

    // Unfollow drops the chat permission server-side, so confirm first.
    var pendingUnfollow by remember(userId) { mutableStateOf(false) }
    // The read-only interest-note ("Preference details") dialog is owned by
    // ProfileContentMobile, so both own-profile and this sheet behave the same.

    when {
        state.isLoading -> Box(
            modifier = Modifier.fillMaxWidth().mHeight(220),
            contentAlignment = Alignment.Center
        ) {
            // Intentionally empty — no loading indicator (user pref).
        }

        state.error != null -> Box(
            modifier = Modifier.fillMaxWidth().mHeight(220),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                state.error ?: t.profileUserSheetError,
                modifier = Modifier.mFontSize(14).color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }

        else -> ProfileContentMobile(
            state = state,
            cardRepository = container.cardRepository,
            userPostRepository = container.userPostRepository,
            onGoalsPeriodChanged = { viewModel.changeGoalsChartPeriod(it) },
            onAssistsPeriodChanged = { viewModel.changeAssistsChartPeriod(it) },
            onMatchHistoryPageChange = { viewModel.loadMatchHistoryPage(it) },
            isOwnProfile = false,
            isFollowing = state.isFollowing,
            onFollow = {
                if (state.isFollowing) pendingUnfollow = true else viewModel.follow()
            },
            onChat = if (state.isFollowing) {
                {
                    AnalyticsTracker.trackClick("open_chat", screen = "user_profile", params = mapOf("user_id" to userId))
                    val encode: (String) -> String = { s -> js("encodeURIComponent")(s) as String }
                    val params = buildString {
                        append("${Routes.CHAT}?user=$userId")
                        state.user?.fullName?.let { append("&name=${encode(it)}") }
                        state.user?.avatarUrl?.let { append("&avatar=${encode(it)}") }
                    }
                    window.location.assign(params)
                }
            } else null,
            onFollowingClick = { viewModel.openFollowingDialog() },
            onFollowersClick = { viewModel.openFollowersDialog() },
            loadPosts = {
                container.publicUserRepository
                    .getUserPosts(userId, mapOf("page" to "1", "size" to "20"))
                    .getOrNull()?.items.orEmpty()
            },
            loadStories = {
                container.userStoryRepository?.getStoriesByUser(userId)?.getOrNull().orEmpty()
            },
            canCreatePost = false,
            canAddStory = false,
            cardsUserId = userId,
            embeddedInSheet = onDismiss != null,
        )
    }

    // ── Followers / Following lists — mobile sheets, read-only (you can't act
    // on another user's lists). Tapping a row re-targets this profile sheet. ──
    val followRepo = container.followRepository
    if (state.showFollowingDialog && followRepo != null) {
        MobileFollowListSheet(
            mode = com.arena_web.ui.components.overlay.FollowListMode.Following,
            initialCount = state.followingCount ?: 0L,
            loadPage = { page, search ->
                val params = buildMap {
                    put("page", "$page")
                    put("size", "20")
                    if (!search.isNullOrBlank()) put("search", search)
                }
                followRepo.getFollowing(userId, params)
            },
            onUserClick = { u ->
                viewModel.closeFollowingDialog()
                UserProfileSheetController.open(u.userId)
            },
            onDismiss = { viewModel.closeFollowingDialog() },
            readOnly = true,
        )
    }
    if (state.showFollowersDialog && followRepo != null) {
        MobileFollowListSheet(
            mode = com.arena_web.ui.components.overlay.FollowListMode.Followers,
            initialCount = state.followersCount ?: 0L,
            loadPage = { page, search ->
                val params = buildMap {
                    put("page", "$page")
                    put("size", "20")
                    if (!search.isNullOrBlank()) put("search", search)
                }
                followRepo.getFollowers(userId, params)
            },
            onUserClick = { u ->
                viewModel.closeFollowersDialog()
                UserProfileSheetController.open(u.userId)
            },
            onDismiss = { viewModel.closeFollowersDialog() },
            readOnly = true,
        )
    }

    // ── Unfollow confirmation ──
    if (pendingUnfollow) {
        val u = state.user
        if (u == null) {
            pendingUnfollow = false
        } else {
            val confirmUser = com.arena_web.data.dto.FollowUserDto(
                userId = u.id,
                username = u.username,
                fullName = u.fullName,
                avatarUrl = u.avatarUrl,
                cardImageUrl = u.mainProfileCard?.cardImageUrl,
                cardAvatarUrl = u.mainProfileCard?.avatarUrl,
                followedAt = "",
            )
            val name = u.fullName?.takeIf { it.isNotBlank() }
                ?: u.username.takeIf { !it.contains('@') }
                ?: t.profileUserSheetThisUser
            MobileUnfollowConfirmSheet(
                user = confirmUser,
                title = t.profileUserSheetUnfollowTitle(name),
                subtitle = t.profileUserSheetRefollowHint,
                confirmLabel = t.profileUserSheetUnfollow,
                onConfirm = {
                    viewModel.unfollow()
                    pendingUnfollow = false
                },
                onCancel = { pendingUnfollow = false },
            )
        }
    }

}
